/**
 * Natal chart screen.
 *
 * Layout matches mobile.html screen 3:
 *   - Head: back arrow + title + birth meta.
 *   - 3-tab switcher (Натал / Транзиты / Прогр.).
 *   - SVG wheel (NatalWheel, pinch-zoomable via CSS `touch-action`).
 *   - Planet chips grid — TAPPABLE: tap a planet → explanation sheet.
 *   - Aspect summary list — TAPPABLE: tap an aspect → explanation sheet.
 *   - "Подробнее" CTA → whole-chart overview modal.
 *
 * Interaction (Wave 6.7, the Mini App's core wow): tapping any planet or aspect
 * opens a dignified RU explanation panel (ExplanationSheet). Haptic feedback
 * fires on tap via the legacy global in lib/tg.ts.
 *
 * Data: fetchChart() — mock fixture Day 1-3; same shape as the real API.
 */
import { useQuery } from "@tanstack/react-query";
import { type ReactElement, useState } from "react";

import { ChartHead } from "@/components/ChartHead";
import {
  ASPECT_KIND,
  ASPECT_SHORT,
  ExplanationSheet,
  hapticSelection,
  type SheetTarget,
} from "@/components/ExplanationSheet";
import { NatalWheel } from "@/components/NatalWheel";
import { PlanetChip } from "@/components/PlanetChip";
import { TabBar } from "@/components/TabBar";
import { type ChartResponse, fetchChart, type Planet } from "@/lib/api";
import { hapticImpact } from "@/lib/tg";
import { useTgContext } from "@/state/tg";

type Tab = "natal" | "transits" | "progressed";

export default function ChartPage(): ReactElement {
  const tg = useTgContext();
  const [tab, setTab] = useState<Tab>("natal");
  /** Tapped chart element (planet or aspect), or "all" for the overview, or null. */
  const [sheet, setSheet] = useState<SheetTarget | "all" | null>(null);

  const { data, isLoading, error } = useQuery<ChartResponse>({
    queryKey: ["chart", tg.initData ?? "guest"],
    queryFn: () => fetchChart(tg.initData),
    staleTime: 30 * 60_000,
  });

  return (
    <main className="screen">
      <div className="body-pad has-tabbar">
        <ChartHead title="Моя карта" meta={data?.meta ?? "Профиль не заполнен"} />

        <div className="chart-tabs" role="tablist">
          <TabButton label="Натал" active={tab === "natal"} onSelect={() => setTab("natal")} />
          <TabButton
            label="Транзиты"
            active={tab === "transits"}
            onSelect={() => setTab("transits")}
          />
          <TabButton
            label="Прогр."
            active={tab === "progressed"}
            onSelect={() => setTab("progressed")}
          />
        </div>

        {/* The wheel is the user's real chart — render it only once we have
            one. An empty ring beats a chart-shaped illustration of nobody. */}
        {data ? (
          <div className="wheel-wrap">
            <NatalWheel planets={data.planets} aspects={data.aspects} wheel={data.wheel} />
          </div>
        ) : null}

        {isLoading ? (
          <div aria-busy="true" style={{ opacity: 0.5, textAlign: "center", padding: 16 }}>
            Загружаем карту…
          </div>
        ) : null}

        {error ? (
          <div className="ob-error" role="alert">
            Не удалось загрузить карту: {(error as Error).message}
          </div>
        ) : null}

        {data ? (
          <>
            <p
              style={{
                margin: "0 0 10px",
                fontSize: 11,
                lineHeight: 1.5,
                color: "var(--fg-tertiary)",
                textAlign: "center",
              }}
            >
              Нажмите на планету или аспект — расскажем, что это значит.
            </p>

            <div className="planet-list">
              {data.planets.map((p: Planet) => (
                <PlanetChip
                  key={p.key}
                  glyph={p.glyph}
                  name={p.name}
                  degree={p.degree}
                  onSelect={() => {
                    hapticSelection();
                    setSheet({ kind: "planet", planet: p });
                  }}
                />
              ))}
            </div>

            <h2 className="section-h" style={{ marginTop: 18 }}>
              <span>Главные аспекты</span>
            </h2>

            <div className="aspect-summary">
              {data.aspects.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className="aspect-item"
                  onClick={() => {
                    hapticSelection();
                    setSheet({ kind: "aspect", aspect: a });
                  }}
                >
                  <div className="head">
                    <span className="title">{a.title}</span>
                    <span
                      className="badge"
                      style={{ background: ASPECT_KIND[a.kind].bg, color: ASPECT_KIND[a.kind].fg }}
                    >
                      {ASPECT_SHORT[a.kind]}
                    </span>
                  </div>
                  <div className="detail">{a.detail}</div>
                </button>
              ))}
            </div>

            <button
              type="button"
              className="chart-details-btn"
              onClick={() => {
                hapticImpact("medium");
                setSheet("all");
              }}
            >
              Подробнее о карте
            </button>
          </>
        ) : null}
      </div>
      <TabBar active="chart" />

      {sheet === "all" ? (
        <ChartOverviewModal data={data} onClose={() => setSheet(null)} />
      ) : sheet ? (
        <ExplanationSheet target={sheet} onClose={() => setSheet(null)} />
      ) : null}
    </main>
  );
}

function TabButton({
  label,
  active,
  onSelect,
}: {
  readonly label: string;
  readonly active: boolean;
  readonly onSelect: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={`t${active ? " active" : ""}`}
      onClick={() => {
        hapticSelection();
        onSelect();
      }}
    >
      {label}
    </button>
  );
}

function ChartOverviewModal({
  data,
  onClose,
}: {
  readonly data: ChartResponse | undefined;
  readonly onClose: () => void;
}): ReactElement {
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop click-to-close; keyboard users use Понятно button or Escape
    <div className="chart-modal" role="dialog" aria-modal="true" onClick={onClose}>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation only; no interactive intent */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: stops backdrop close when panel clicked */}
      <div className="panel" onClick={(e) => e.stopPropagation()}>
        <h3>Карта целиком</h3>
        <p>
          Натальная карта рассчитана по Swiss Ephemeris с точностью до 1 угловой секунды. Учтены 14
          планет, лунные узлы и главные неподвижные звёзды.
        </p>
        {data ? (
          <p>
            В вашей карте {data.planets.length} планет и {data.aspects.length} ключевых аспектов.
            Кабинет в браузере покажет полный текст: оси, дома, конфигурации, диспозиции.
          </p>
        ) : null}
        <button type="button" className="close-btn" onClick={onClose}>
          Понятно
        </button>
      </div>
    </div>
  );
}
